import React, { Fragment } from 'react';
import { injectIntl } from 'react-intl';
import { Grid, useMediaQuery, Box, Button } from '@mui/material';
import InputPanel from './InputPanel/InputPanel';
import SideLoanInterestInput from './SideLoanInterestInput/SideLoanInterestInput';
import GraphCard from './GraphCard/GraphCard';
import SideInput from './SideInput/SideInput';
import { getProfitByYear } from './calculator';
import { useTheme } from '@mui/material/styles';
import PFinTable from './PFinTable/PFinTable';
import CalculatorProfiles from './CalculatorProfiles/CalculatorProfiles';
import { getWebImageUrl } from '../../../../utils/image';
import AdviserInfo from '../../../Export/AdviserInfo/AdviserInfo';
import _ from 'lodash';
import PoweredBy from '../../../PolicyReview/PoweredBy/PoweredBy';
import { getPremiumFinancingProfileDisplay } from '../../../../utils/premium-financing';
import { getUserLogo } from '../../../../utils/user';
import { ADVISER_LOGO_FUNCTIONS } from '../../../../constants/user';
import CurrencyInputPanel from './CurrencyInputPanel/CurrencyInputPanel';
import IRRCard from './IRRCard/IRRCard';
import AladdinHint from '../../../../components/AladdinHint/AladdinHint';
import { CampaignCodeControl } from '../../../../components/CampaignCodeControl/CampaignCodeControl';
import { useState } from 'react';
import ClickableContentDisplayDialog from './ClickableContentDisplayDialog/ClickableContentDisplayDialog';

const PremiumFinancing = props => {
  const {
    intl,
    adviser,
    pFinProfiles,
    principal: originalPrincipal,
    base: originalBase,
    loanPercentage,
    bankLoanInterest,
    policyInterest,
    term,
    // Default the iterable fields: the PDF template renders this component directly
    // with the injected payload, which may be incomplete. `yearsToShow.filter(...)`
    // below (and the calculator) would otherwise throw and crash the whole render.
    yearsToShow = [],
    cashValueAt: originalCashValueAt,
    firstYearBonus: originalFirstYearBonus,
    termLoanPercentage,
    termAddOn: originalTermAddOn,
    overrideLoanInterest,
    graphMode,
    rowOpen,
    fixedLoanInterestExpanded,
    dynamicLoanInterestExpanded,
    isCurrencyEnabled = false,
    currency = 7.8,
    showIRR = false,
    displayName,
    extraSideInputs,
    template,
    disableLoan,
    handleChange,
    handleCheckedChange,
    handleProfileChange,
    profileIndex,
    saveProfiles,
    onChangeGraphMode,
    onToggleOpen,
    onToggleFixedLoanInterestExpanded,
    onToggleDynamicLoanInterestExpanded,
    onToggleDisableLoan,
    overrideLoanInterestSliderValue,
    openLoanCalculatorDialog,
    openBonusCalculatorDialog,
    openLoanInterestCalculatorDialog,
    openEditDisplayNamesDialog,
    isPrivateMode,
    trial,
    isGuest
  } = props;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmallerScreen = useMediaQuery('(max-width: 499px)');
  const spacing = isSmallScreen ? 2 : 2;

  const adviserTeamLogoUrl = _.get(adviser, 'teamLogoUrl');
  const userLogo = getUserLogo(adviser, adviserTeamLogoUrl, ADVISER_LOGO_FUNCTIONS.PDF);
  const showTeamLogo = !!userLogo;

  // adjust inputs according to currency settings
  const factor = isCurrencyEnabled ? currency : 1;
  const principal = originalPrincipal * factor;
  const base = originalBase * factor;
  const cashValueAt = (originalCashValueAt || []).map(e => e * factor);
  const firstYearBonus = originalFirstYearBonus * factor;
  const termAddOn = (originalTermAddOn || []).map(e => e * factor);

  const yearlyData = getProfitByYear({
    principal,
    base,
    bankLoanInterest,
    yearsToShow,
    cashValueAt,
    termLoanPercentage,
    termAddOn,
    firstYearBonus,
    overrideLoanInterest,
    disableLoan
  });
  const years = yearsToShow.filter((e, i) => i === 0 || (e && cashValueAt[i]));
  const loan = (principal ?? 0) - (base ?? 0) - (firstYearBonus ?? 0);
  const [selectedRow, setSelectedRow] = useState(undefined);

  const inputPanel = (
    <InputPanel
      intl={intl}
      principal={principal}
      base={base}
      loanPercentage={loanPercentage}
      firstYearBonus={firstYearBonus}
      handleChange={handleChange}
      openLoanCalculatorDialog={openLoanCalculatorDialog}
      openBonusCalculatorDialog={openBonusCalculatorDialog}
      template={template}
      disableLoan={disableLoan}
      onToggleDisableLoan={onToggleDisableLoan}
      isCurrencyEnabled={isCurrencyEnabled}
      currency={currency}
    />
  );

  const graphCard = (
    <GraphCard
      intl={intl}
      yearsToShow={yearsToShow}
      principal={principal}
      base={base}
      data={yearlyData}
      firstYearBonus={firstYearBonus}
      isSmallScreen={isSmallScreen}
      isSmallerScreen={isSmallerScreen}
      graphMode={graphMode}
      onChangeGraphMode={onChangeGraphMode}
      template={template}
      openEditDisplayNamesDialog={openEditDisplayNamesDialog}
      displayName={getPremiumFinancingProfileDisplay(intl, { displayName: displayName }, profileIndex)}
    />
  );

  const pFinTable = (
    <PFinTable
      principal={principal}
      base={base}
      bankLoanInterest={bankLoanInterest}
      firstYearBonus={firstYearBonus}
      yearlyData={yearlyData}
      years={years}
      overrideLoanInterest={overrideLoanInterest}
      intl={intl}
      rowOpen={rowOpen}
      onToggleOpen={onToggleOpen}
      profileIndex={profileIndex}
      showIRR={showIRR}
      onSelectRow={selectedRow => setSelectedRow(selectedRow)}
    />
  );

  const currencyInputPanel = (
    <CurrencyInputPanel
      enabled={isCurrencyEnabled}
      currency={currency}
      showIRR={showIRR}
      linkedIllustration={props.linkedIllustration}
      returnRealizationRateEnabled={props.returnRealizationRateEnabled}
      onToggle={handleCheckedChange}
      onReturnRealizationRateToggle={event =>
        handleChange({
          target: {
            name: 'returnRealizationRateEnabled',
            value: event.target.checked
          }
        })
      }
      onChange={currency => handleChange({ target: { name: 'currency', value: currency } })}
      template={template}
    />
  );

  const sideLoanInterestInput = (
    <SideLoanInterestInput
      intl={intl}
      bankLoanInterest={bankLoanInterest}
      term={term}
      overrideLoanInterest={overrideLoanInterest}
      termLoanPercentage={termLoanPercentage}
      termAddOn={termAddOn}
      yearsToShow={yearsToShow}
      handleChange={handleChange}
      handleCheckedChange={handleCheckedChange}
      loan={loan}
      fixedLoanInterestExpanded={fixedLoanInterestExpanded}
      onToggleFixedLoanInterestExpanded={onToggleFixedLoanInterestExpanded}
      dynamicLoanInterestExpanded={dynamicLoanInterestExpanded}
      onToggleDynamicLoanInterestExpanded={onToggleDynamicLoanInterestExpanded}
      overrideLoanInterestSliderValue={overrideLoanInterestSliderValue}
      template={template}
      isCurrencyEnabled={isCurrencyEnabled}
      isSmallScreen={isSmallScreen}
      openLoanInterestCalculatorDialog={openLoanInterestCalculatorDialog}
    />
  );

  const sideInput = (
    <SideInput
      intl={intl}
      principal={principal}
      term={term}
      yearsToShow={yearsToShow}
      cashValueAt={cashValueAt}
      loanPercentage={loanPercentage}
      firstYearBonus={firstYearBonus}
      returnRealizationRate={props.returnRealizationRate}
      returnRealizationRateEnabled={props.returnRealizationRateEnabled}
      linkedIllustration={props.linkedIllustration}
      profileId={props._id}
      handleChange={handleChange}
      isCurrencyEnabled={isCurrencyEnabled}
      currency={currency}
      template={template}
      isSmallScreen={isSmallScreen}
    />
  );

  const calculatorProfiles = (
    <CalculatorProfiles
      intl={intl}
      pFinProfiles={pFinProfiles}
      index={profileIndex}
      onIndexChange={handleProfileChange}
      saveProfiles={saveProfiles}
      isSmallScreen={isSmallScreen}
      extraSideInputs={extraSideInputs}
      openEditDisplayNamesDialog={openEditDisplayNamesDialog}
    />
  );

  const nonTemplateDisclaimer = (
    <div className="disclaimer">
      {intl.formatMessage({ id: 'pfa-disclaimer' })}
      {!showIRR && `\n${intl.formatMessage({ id: 'pfa-disclaimer-annual-return' })}`}
      {showIRR && `\n${intl.formatMessage({ id: 'pfa-disclaimer-irr' })}`}
    </div>
  );

  const featHL = !template && (
    <CampaignCodeControl
      userCampaignCodes={adviser.campaignCodes}
      targetCampaignCode={'FEATURE_HL'}
      children={
        <ClickableContentDisplayDialog
          selectedRow={selectedRow}
          open={!!selectedRow}
          onClose={() => setSelectedRow(undefined)}
          yearlyData={yearlyData}
          years={years}
          loan={loan}
          base={base}
          bankLoanInterest={bankLoanInterest}
          firstYearBonus={firstYearBonus}
          overrideLoanInterest={overrideLoanInterest}
          termLoanPercentage={termLoanPercentage}
          termAddOn={termAddOn}
          term={term}
          yearsToShow={yearsToShow}
          principal={principal}
          intl={intl}
        />
      }
    />
  );

  if (!isSmallScreen) {
    return (
      <Fragment>
        {template && !isPrivateMode && (
          <Grid
            className="header"
            container
            sx={{ alignItems: 'center', justifyContent: 'flex-end' }}
            style={
              template
                ? {
                    backgroundImage: `url("${getWebImageUrl('img/policy-review/header_v2-right-3.png')}")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'auto 145px',
                    backgroundPosition: 'bottom 0 right 0',
                    paddingBottom: 24,
                    marginBottom: 16,
                    position: 'relative',
                    minHeight: 145
                  }
                : undefined
            }
          >
            <div
              style={
                template
                  ? {
                      position: 'absolute',
                      height: '100%',
                      width: '100%',
                      top: 0,
                      backgroundImage: `url("${getWebImageUrl('img/policy-review/header_v2-left.png')}")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: 'auto 145px',
                      backgroundPosition: `bottom 0 left 0`
                    }
                  : undefined
              }
            />

            {template && (
              <Grid style={{ flexGrow: 1 }}>
                <div
                  style={{
                    width: 'calc(100% - 510px)',
                    minWidth: showTeamLogo ? 260 : 195,
                    float: 'right',
                    textAlign: 'center'
                  }}
                >
                  {showTeamLogo ? (
                    <img
                      src={userLogo}
                      alt="logo"
                      style={{ maxHeight: 150, maxWidth: 260, width: 'auto', objectFit: 'contain' }}
                    />
                  ) : (
                    <img src={getWebImageUrl('img/policy-review/PFPLogoSummary.png')} alt="portfoplus" width={195} />
                  )}
                </div>
              </Grid>
            )}

            <Grid>
              <AdviserInfo
                show={true}
                showQRCode={true}
                adviser={adviser}
                direction="row"
                QRActionButton="create-one-for-free"
                qrCodeWidth={150}
                referLinkParams={{
                  source: 'pdf',
                  extraSearch: {
                    defaultMessage: intl.formatMessage({ id: 'pfin-default-msg' })
                  }
                }}
              />
            </Grid>
          </Grid>
        )}

        {!template && featHL}

        <Grid container spacing={spacing}>
          <Grid size={9}>
            <Grid container spacing={spacing}>
              <Grid size={12}>{inputPanel}</Grid>
              <Grid size={12}>{graphCard}</Grid>
              <Grid size={12}>{pFinTable}</Grid>
            </Grid>
            {!template && nonTemplateDisclaimer}
          </Grid>
          <Grid size={3}>
            <Grid container spacing={template ? 1.5 : spacing}>
              {(!template || isCurrencyEnabled) && <Grid size={12}>{currencyInputPanel}</Grid>}
              <Grid size={12}>{sideInput}</Grid>
              <Grid size={12}>{sideLoanInterestInput}</Grid>
              {!template && <Grid size={12}>{calculatorProfiles}</Grid>}
              {(!trial || !isGuest) && !template && (
                <Grid size={12}>
                  <AladdinHint title={intl.formatMessage({ id: 'aladdin-demo-video' })} helpCenter="t09" />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
        {template && (
          <Grid container sx={{ justifyContent: 'space-between' }} spacing={spacing}>
            <Grid>{nonTemplateDisclaimer}</Grid>
            <Grid>
              <PoweredBy />
            </Grid>
          </Grid>
        )}
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Grid container spacing={spacing}>
        {(!trial || !isGuest) && !template && (
          <Grid size={12}>
            <AladdinHint title={intl.formatMessage({ id: 'aladdin-demo-video' })} helpCenter="t09" />
          </Grid>
        )}
        <Grid size={12}>{inputPanel}</Grid>
        {(!template || isCurrencyEnabled) && <Grid size={12}>{currencyInputPanel}</Grid>}
        {!template && featHL}

        <Grid size={12}>{sideLoanInterestInput}</Grid>
        <Grid size={12}>{sideInput}</Grid>
        <Grid size={12}>{graphCard}</Grid>
        <Grid size={12}>{pFinTable}</Grid>
        <Grid size={12}>{calculatorProfiles}</Grid>
        {!template && (
          <Box className="disclaimer" style={{ padding: '0 8px 36px 8px' }}>
            {nonTemplateDisclaimer}
          </Box>
        )}
      </Grid>
    </Fragment>
  );
};

export default injectIntl(PremiumFinancing);
